import { paginateSupabase } from '@/lib/supabase-paginate';
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, SUPABASE_URL_EXPORT } from '@/integrations/supabase/client';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

import { Badge } from '@/components/ui/badge';

import { Upload, Search, ChevronLeft, ChevronRight, ArrowDownLeft, ArrowUpRight, BadgePercent, Download, Hash } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

import { useBankTransactions } from '@/hooks/finance/useBankTransactions';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useQuery } from '@tanstack/react-query';
import { convertBalance } from '@/hooks/finance/useConsolidatedBalances';

import { ManualTransactionDialog } from '@/components/finance/banking/import_bank_statements/ManualTransactionDialog';

import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

const ITEMS_PER_PAGE = 50;

type UIBankTx = {
  id: string;
  bank_account_id: string;
  transaction_date: string;
  import_batch_id: string | null;

  type: string | null;
  reference: string | null;
  description: string | null;

  amount: number; // signed
  amountCurrency: string;

  fee: number | null;
  feeCurrency: string; 

  balance: number | null;
  balanceCurrency: string;

  raw: any;
};

const validCurrencyCodes = new Set([
  'USD', 'EUR', 'GBP', 'ETB', 'AED', 'SAR', 'KES', 'TZS', 'UGX', 'RWF',
  'ZAR', 'EGP', 'NGN', 'GHS', 'INR', 'CNY', 'JPY', 'KRW', 'CAD', 'AUD',
  'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'TRY', 'BRL', 'MXN'
]);

const toNumber = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const formatCurrency = (amount: number | null | undefined, currency: string) => {
  const currencyCode = currency || 'ETB';
  const safeAmount = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  const absAmount = Math.abs(safeAmount);

  if (validCurrencyCodes.has(currencyCode)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absAmount);
  }

  return `${absAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currencyCode}`;
};

const TxTypeBadge = ({ type }: { type: string | null }) => {
  if (!type) return null;

  const t = type.toLowerCase();

  if (t === 'credit') {
    return (
      <Badge
        variant="outline"
        className="text-xs border-green-600/30 bg-green-50 text-green-700"
      >
        <ArrowDownLeft className="h-3.5 w-3.5 mr-1" />
        Credit
      </Badge>
    );
  }

  if (t === 'debit') {
    return (
      <Badge
        variant="outline"
        className="text-xs border-red-600/30 bg-red-50 text-red-700"
      >
        <ArrowUpRight className="h-3.5 w-3.5 mr-1" />
        Debit
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="text-xs">
      {type}
    </Badge>
  );
};

const normalizeSignedAmountByType = (amountRaw: number, type: string | null) => {
  const abs = Math.abs(Number.isFinite(amountRaw) ? amountRaw : 0);
  const t = (type || "").toLowerCase();

  if (t === "credit") return +abs;
  if (t === "debit") return -abs;

  return amountRaw;
};

const BankTransactionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: profile } = useAuth();
  const [importIdToName, setImportIdToName] = useState<Record<string, string>>({});
  const [namesLoading, setNamesLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  useEffect(() => {
  const params = new URLSearchParams(location.search);
  const acc = params.get('account');

  if (acc && acc !== selectedAccount) {
    setSelectedAccount(acc);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.search]);

  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [bookingFees, setBookingFees] = useState(false);

  const handleBookFees = async () => {
    setBookingFees(true);
    try {
      const { data, error } = await supabase.functions.invoke('bank-fees-autobook', {
        body: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          account_ids: selectedAccount !== 'all' ? [selectedAccount] : undefined,
        },
      });
      if (error) throw error;
      const created = (data as any)?.created ?? 0;
      const skipped = (data as any)?.skipped ?? 0;
      toast({
        title: created > 0 ? `Booked ${created} fee expense${created === 1 ? '' : 's'}` : 'No new fees to book',
        description: `${skipped} fee${skipped === 1 ? '' : 's'} already booked in this range.`,
      });
    } catch (e: any) {
      toast({
        title: 'Could not book fees',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBookingFees(false);
    }
  };
  const { data: bankAccounts = [] } = useBankAccounts(true);
  const { data: transactions = [], isLoading: transactionsLoading, refetch: refetchTransactions } =
    useBankTransactions({
      bankAccountId: selectedAccount !== 'all' ? selectedAccount : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      search: search || undefined,
      page: currentPage,
      pageSize: ITEMS_PER_PAGE
    });

  const getAccountName = (accountId: string) => {
    const account = bankAccounts.find(a => a.id === accountId);
    return account?.name || 'Unknown';
  };

  // Normalize DB rows -> UI model (handles numeric strings, new column names, currency split)
  const normalizedTransactions: UIBankTx[] = useMemo(() => {
    return (transactions as any[]).map((tx) => {
      const amountRaw = toNumber(tx.transaction_amount) ?? 0;
      const type = tx.transaction_type ?? null;

      const amount = normalizeSignedAmountByType(amountRaw, type);
      const balance = toNumber(tx.end_balance);

      const amountCurrency = tx.transaction_amount_currency || 'ETB';
      const balanceCurrency = tx.end_balance_currency || amountCurrency || 'ETB';

      const fee = toNumber(tx.transaction_fee);
      const feeCurrency = tx.transaction_fee_currency || amountCurrency || 'ETB';

      return {
        id: tx.id,
        bank_account_id: tx.bank_account_id,
        transaction_date: tx.transaction_date,
        import_batch_id: tx.import_batch_id ?? null,

        type,
        reference: tx.transaction_id_reference ?? null,
        description: tx.description_details ?? null,

        amount,
        amountCurrency,

        fee,
        feeCurrency,

        balance,
        balanceCurrency,

        raw: tx.raw_data ?? null
      };
    });
  }, [transactions]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedAccount, search, dateFrom, dateTo]);

  // Summary by currency (over filtered set)
  type Summary = { credit: number; debit: number; fees: number; count: number };
  const summaryByCurrency = useMemo(() => {
    const map = new Map<string, Summary>();
    const get = (cur: string) => {
      let s = map.get(cur);
      if (!s) { s = { credit: 0, debit: 0, fees: 0, count: 0 }; map.set(cur, s); }
      return s;
    };
    for (const tx of normalizedTransactions) {
      const cur = tx.amountCurrency || 'ETB';
      const s = get(cur);
      if (tx.amount > 0) s.credit += tx.amount;
      else if (tx.amount < 0) s.debit += Math.abs(tx.amount);
      s.count += 1;
      if (tx.fee && tx.fee > 0) {
        get(tx.feeCurrency || cur).fees += tx.fee;
      }
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [normalizedTransactions]);

  // Fetch latest exchange rates for converting summary into ETB & USD
  const ratesQuery = useQuery({
    queryKey: ['exchange-rates-all-latest'],
    queryFn: async () => {
      const data = await paginateSupabase<any>((from, to) =>
        supabase
          .from('exchange_rates' as any)
          .select('currency_pair, rate, effective_date')
          .order('effective_date', { ascending: false })
          .range(from, to) as any
      );
      return (data || []) as unknown as Array<{ currency_pair: string; rate: number; effective_date: string }>;
    },
  });

  const summaryTotals = useMemo(() => {
    const rates = ratesQuery.data || [];
    const targets = ['ETB', 'USD'] as const;
    const totals: Record<string, { credit: number; debit: number; fees: number; missing: Set<string> }> = {
      ETB: { credit: 0, debit: 0, fees: 0, missing: new Set() },
      USD: { credit: 0, debit: 0, fees: 0, missing: new Set() },
    };
    for (const [cur, s] of summaryByCurrency) {
      for (const tgt of targets) {
        const cCredit = convertBalance(s.credit, cur, tgt, rates);
        const cDebit = convertBalance(s.debit, cur, tgt, rates);
        const cFees = convertBalance(s.fees, cur, tgt, rates);
        if (cCredit && cDebit) {
          totals[tgt].credit += cCredit.converted;
          totals[tgt].debit += cDebit.converted;
          totals[tgt].fees += cFees?.converted ?? 0;
        } else {
          totals[tgt].missing.add(cur);
        }
      }
    }
    return totals;
  }, [summaryByCurrency, ratesQuery.data]);

  const totalTransactions = normalizedTransactions.length;

  const handleExportCSV = () => {
    const headers = [
      '#', 'Date', 'Account', 'Type', 'Amount', 'Amount Currency',
      'Fee', 'Fee Currency', 'Balance', 'Balance Currency',
      'Transaction Id', 'Description', 'Added by'
    ];
    const escape = (v: any) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = normalizedTransactions.map((tx, idx) => [
      idx + 1,
      tx.transaction_date ? format(new Date(tx.transaction_date), 'yyyy-MM-dd') : '',
      getAccountName(tx.bank_account_id),
      tx.type ?? '',
      tx.amount,
      tx.amountCurrency,
      tx.fee ?? '',
      tx.fee ? tx.feeCurrency : '',
      tx.balance ?? '',
      tx.balance !== null ? tx.balanceCurrency : '',
      tx.reference ?? '',
      tx.description ?? '',
      tx.import_batch_id ? (importIdToName[tx.import_batch_id] ?? '') : '',
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bank-transactions-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Pagination
  const totalCount = normalizedTransactions.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  const paginatedTransactions = normalizedTransactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const from = totalCount === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const to = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

  const paginationBlock = (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        Showing {from} to {to} of {totalCount} transactions
      </p>

      {totalPages > 1 ? (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );


  useEffect(() => {
    if (profile?.id) {
      console.log("User ID from profile:", profile.id);
    }
  }, [profile]);

  useEffect(() => {
  (async () => {
    const { data, error } = await supabase.auth.getSession();

    console.log("=== SUPABASE DEBUG ===");
    console.log("SESSION ERROR:", error);
    console.log("SESSION USER ID:", data.session?.user?.id);
    console.log("SESSION TOKEN EXISTS:", !!data.session?.access_token);
    console.log("SUPABASE URL EXPORT:", SUPABASE_URL_EXPORT);
    console.log("ENV URL:", import.meta.env.VITE_SUPABASE_URL);
    console.log("ENV ANON KEY EXISTS:", !!import.meta.env.VITE_SUPABASE_ANON_KEY);
  })();
}, []);

  useEffect(() => {
    const run = async () => {
      const importIds = Array.from(
        new Set(
          (transactions as any[])
            .map((t) => t.import_batch_id)
            .filter(Boolean)
        )
      ) as string[];

      if (importIds.length === 0) {
        setImportIdToName({});
        return;
      }

      setNamesLoading(true);

      const { data: imports, error: importsError } = await supabase
        .from('bank_statement_imports')
        .select('id, created_by')
        .in('id', importIds);

      if (importsError) {
        console.error('Failed to load imports:', importsError);
        setNamesLoading(false);
        return;
      }

      const userIds = Array.from(
        new Set((imports ?? []).map((x) => x.created_by).filter(Boolean))
      ) as string[];

      if (userIds.length === 0) {
        setImportIdToName({});
        setNamesLoading(false);
        return;
      }

      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (usersError) {
        console.error('Failed to load user profiles:', usersError);
        setNamesLoading(false);
        return;
      }

      const userIdToName = new Map<string, string>();
      (users ?? []).forEach((u) => {
        const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
        userIdToName.set(u.id, full || 'Unknown');
      });

      const map: Record<string, string> = {};
      (imports ?? []).forEach((imp) => {
        const name = userIdToName.get(imp.created_by) || 'Unknown';
        map[imp.id] = name;
      });

      setImportIdToName(map);
      setNamesLoading(false);
    };

    run();
  }, [transactions]);


  return (
    <Layout>
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Bank Transactions</h1>
            <p className="text-muted-foreground">
              View and manage imported bank transactions
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/finance/banking/balances')}
            >
              View Balances
            </Button>

            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={totalTransactions === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>

            <Button onClick={() => setImportDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Import Statement
            </Button>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Transactions</p>
                <Hash className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold mt-1 tabular-nums">{totalTransactions.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {summaryByCurrency.length} {summaryByCurrency.length === 1 ? 'currency' : 'currencies'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Credit</p>
                <ArrowDownLeft className="h-4 w-4 text-green-600" />
              </div>
              <div className="mt-1 space-y-0.5">
                {summaryByCurrency.length === 0 ? (
                  <p className="text-2xl font-bold tabular-nums text-muted-foreground">-</p>
                ) : summaryByCurrency.map(([cur, s]) => (
                  <p key={cur} className="text-base font-semibold tabular-nums text-green-700">
                    {formatCurrency(s.credit, cur)}
                  </p>
                ))}
                {summaryByCurrency.length > 0 && (
                  <div className="pt-1 mt-1 border-t border-border/50 space-y-0.5">
                    {(['ETB', 'USD'] as const).map((tgt) => (
                      <p key={tgt} className="text-xs tabular-nums text-muted-foreground">
                        ≈ {formatCurrency(summaryTotals[tgt].credit, tgt)}
                        {summaryTotals[tgt].missing.size > 0 && ' *'}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Debit</p>
                <ArrowUpRight className="h-4 w-4 text-red-600" />
              </div>
              <div className="mt-1 space-y-0.5">
                {summaryByCurrency.length === 0 ? (
                  <p className="text-2xl font-bold tabular-nums text-muted-foreground">-</p>
                ) : summaryByCurrency.map(([cur, s]) => (
                  <p key={cur} className="text-base font-semibold tabular-nums text-red-700">
                    {formatCurrency(s.debit, cur)}
                  </p>
                ))}
                {summaryByCurrency.length > 0 && (
                  <div className="pt-1 mt-1 border-t border-border/50 space-y-0.5">
                    {(['ETB', 'USD'] as const).map((tgt) => (
                      <p key={tgt} className="text-xs tabular-nums text-muted-foreground">
                        ≈ {formatCurrency(summaryTotals[tgt].debit, tgt)}
                        {summaryTotals[tgt].missing.size > 0 && ' *'}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Total Fees</p>
                <BadgePercent className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="mt-1 space-y-0.5">
                {summaryByCurrency.filter(([, s]) => s.fees > 0).length === 0 ? (
                  <p className="text-2xl font-bold tabular-nums text-muted-foreground">-</p>
                ) : summaryByCurrency.filter(([, s]) => s.fees > 0).map(([cur, s]) => (
                  <p key={cur} className="text-base font-semibold tabular-nums">
                    {formatCurrency(s.fees, cur)}
                  </p>
                ))}
                {summaryByCurrency.some(([, s]) => s.fees > 0) && (
                  <div className="pt-1 mt-1 border-t border-border/50 space-y-0.5">
                    {(['ETB', 'USD'] as const).map((tgt) => (
                      <p key={tgt} className="text-xs tabular-nums text-muted-foreground">
                        ≈ {formatCurrency(summaryTotals[tgt].fees, tgt)}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 w-full"
                disabled={bookingFees}
                onClick={handleBookFees}
              >
                {bookingFees ? 'Booking…' : 'Book unbooked fees'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* FILTERS */}
        <div className="flex flex-wrap gap-4 items-end">

          <div className="w-64">
            <label className="text-sm font-medium mb-1.5 block">
              Bank Account
            </label>

            <Select
              value={selectedAccount}
              onValueChange={setSelectedAccount}
            >
              <SelectTrigger>
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {bankAccounts.map(account => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-40">
            <label className="text-sm font-medium mb-1.5 block">
              From Date
            </label>
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </div>

          <div className="w-40">
            <label className="text-sm font-medium mb-1.5 block">
              To Date
            </label>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-medium mb-1.5 block">
              Search
            </label>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search description, reference..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

        </div>
        {/* PAGINATION TOP */}
        {paginationBlock}
        {/* TABLE */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[56px] text-right">#</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Fees</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Transaction Id</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Added by</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {transactionsLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    Loading transactions...
                  </TableCell>
                </TableRow>
              ) : paginatedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    No transactions found. Import a bank statement to get started.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTransactions.map((tx, idx) => {
                  const rowNo = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;

                  return (
                    <TableRow key={tx.id}>
                      <TableCell className="text-right text-muted-foreground tabular-nums">
                        {rowNo}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        {format(new Date(tx.transaction_date), 'dd MMM yyyy')}
                      </TableCell>

                      <TableCell>
                        <span className="text-sm">
                          {getAccountName(tx.bank_account_id)}
                        </span>
                      </TableCell>

                      <TableCell>
                        {tx.type && <TxTypeBadge type={tx.type} />}
                      </TableCell>

                      <TableCell className="text-right whitespace-nowrap">
                        <span
                          className={cn(
                            'font-medium',
                            tx.amount > 0 ? 'text-green-600' : tx.amount < 0 ? 'text-red-600' : 'text-muted-foreground'
                          )}
                        >
                          {tx.amount > 0 ? '+' : tx.amount < 0 ? '−' : ''}
                          {formatCurrency(Math.abs(tx.amount), tx.amountCurrency)}
                        </span>
                      </TableCell>

                      <TableCell className="text-right whitespace-nowrap">
  {tx.fee !== null && tx.fee > 0 ? (
    <span className="inline-flex items-center gap-1 text-muted-foreground tabular-nums">
      <BadgePercent className="h-3.5 w-3.5" />
      {formatCurrency(tx.fee, tx.feeCurrency)}
    </span>
  ) : (
    <span className="text-muted-foreground">-</span>
  )}
</TableCell>

                      <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                        {tx.balance !== null
                          ? formatCurrency(tx.balance, tx.balanceCurrency)
                          : '-'}
                      </TableCell>

                      <TableCell className="max-w-[220px] truncate" title={tx.reference || ''}>
                        {tx.reference || '-'}
                      </TableCell>

                      <TableCell className="max-w-[300px] truncate" title={tx.description || ''}>
                        {tx.description || '-'}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        {tx.import_batch_id
                          ? (importIdToName[tx.import_batch_id] ?? (namesLoading ? 'Loading...' : '-'))
                          : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* PAGINATION BOTTOM */}
        {paginationBlock}

      </div>

      {/* IMPORT DIALOG */}
      <ManualTransactionDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        bankAccounts={bankAccounts}
        userId={profile?.id}
        onImportSuccess={refetchTransactions}
      />
    </Layout>
  );
};

export default BankTransactionsPage;
